import * as z from 'zod';

import { normalizeToUtc } from '@repo/utils';

const businessUnitSchema = z.object({
    buCode: z.string().optional(),
});

const deliverySchema = z.object({
    deliveryId: z.string().optional(),
    serviceItemId: z.string().optional(),
});

const orderSchema = z.object({
    orderNo: z.string(),
});

const shippingAddressSchema = z.object({
    zipCode: z.string().optional(),
    countryCode: z.string().optional(),
});

const timeWindowItemSchema = z.object({
    itemId: z.string(),
    itemName: z.string().optional(),
    lineId: z.union([z.string(), z.number()]).optional(),
    qty: z.number().optional(),
    qtyType: z.string().optional(),
});

const providedServiceArticleSchema = z.object({
    itemNo: z.string(),
    quantity: z.number(),
});

export const transportTimewindowSchema = z.object({
    transportTimewindowId: z.string(),
    start: z.string(),
    end: z.string(),
    serviceIdentifier: z.string().optional(),
});

export type TransportTimewindow = z.infer<typeof transportTimewindowSchema>;

const providedServiceSchema = z.object({
    transportTimewindows: z.array(transportTimewindowSchema),
    serviceProviderId: z.string(),
    capacityNeeded: z.number(),
    capacityUnit: z.string(),
    serviceItemNo: z.string(),
    serviceProductId: z.string(),
    articles: z.array(providedServiceArticleSchema),
});

export const timeWindowsPayloadSchema = z.object({
    businessUnit: businessUnitSchema,
    fromDate: z.string(),
    integrator: z.string(),
    locale: z.string(),
    shippingAddress: shippingAddressSchema,
    userId: z.string().optional(),
    items: z.array(timeWindowItemSchema).optional(),
    // Delivery-specific fields (optional)
    delivery: deliverySchema.optional(),
    order: orderSchema.optional(),
    // Provided service-specific fields (optional)
    providedService: providedServiceSchema.optional(),
});

export type TimeWindowsPayload = z.infer<typeof timeWindowsPayloadSchema>;

// ============================================
// Time Windows API Response types
// ============================================

const unitSchema = z.object({
    type: z.string(),
    code: z.string(),
    name: z.string().optional(),
    phoneNo: z.string().optional(),
});

const itemLineSchema = z.object({
    isExceptionalQty: z.string(),
    shipNode: z.string(),
    id: z.string(),
});

const timeWindowDetailSchema = z.object({
    timeWindowId: z.string(),
    start: z.string(),
    end: z.string(),
    capacityAllocated: z.number().optional(),
});

const rawTimeWindowResponseItemSchema = z.object({
    // Required fields (present in both delivery and provided service)
    timeWindowId: z.string(),
    fromDateTime: z.string(),
    toDateTime: z.string(),
    // Delivery-specific fields (optional for provided services)
    date: z.string().optional(),
    startTime: z.string().optional(),
    endTime: z.string().optional(),
    tspName: z.string().optional(),
    orderNo: z.string().optional(),
    hasDeliveryExceptionalQty: z.string().optional(),
    transferringUnit: unitSchema.optional(),
    fulfillingUnit: unitSchema.optional(),
    capacityAvailable: z.boolean().optional(),
    node: z.string().optional(),
    resourcePoolId: z.string().optional(),
    id: z.string().optional(),
    resourceId: z.string().optional(),
    nodeId: z.string().optional(),
    serviceProviderId: z.string().optional(),
    serviceProviderName: z.string().optional(),
    timeZoneId: z.string().optional(),
    timeZone: z.string().optional(),
    isExceptionalVolume: z.boolean().optional(),
    isExceptionalQty: z.boolean().optional(),
    itemLines: z.array(itemLineSchema).optional(),
    paymentCutOffDateTime: z.string().optional(),
    tspID: z.string().optional(),
    fromDateTimeUtc: z.string().optional(),
    toDateTimeUtc: z.string().optional(),
    // Provided service-specific fields
    bookable: z.boolean().optional(),
    capacityAllocated: z.number().optional(),
    dateTimeFrom: z.string().optional(),
    dateTimeTo: z.string().optional(),
    status: z.string().optional(),
    willBeOverbooked: z.boolean().optional(),
    allowToOverbook: z.boolean().optional(),
    timeWindowDetails: z.array(timeWindowDetailSchema).optional(),
});

export const timeWindowResponseItemSchema = rawTimeWindowResponseItemSchema.transform((tw) => {
    const timeZone = tw.timeZoneId ?? tw.timeZone;
    if (!timeZone) {
        throw new Error(`Time window ${tw.timeWindowId} is missing timezone`);
    }
    const { fromDateTime, toDateTime, paymentCutOffDateTime, ...rest } = tw;
    return {
        ...rest,
        fromDateTimeUtc: normalizeToUtc(fromDateTime, timeZone),
        toDateTimeUtc: normalizeToUtc(toDateTime, timeZone),
        timeZone,
        paymentCutOffDateTimeUtc: paymentCutOffDateTime ? normalizeToUtc(paymentCutOffDateTime, timeZone) : undefined,
    };
});

export const timeWindowsResponseSchema = z.object({
    timeWindows: z.array(timeWindowResponseItemSchema),
});

export type TimeWindowResponseItem = z.infer<typeof timeWindowResponseItemSchema>;
export type TimeWindowsResponse = z.infer<typeof timeWindowsResponseSchema>;

// ============================================
// Frontend-owned TimeWindow type
// Normalized for internal use across all asset types
// ============================================

const fulfillmentUnitSchema = z.object({
    code: z.string().optional(),
    type: z.string().optional(),
});

export const timeWindowSchema = z.object({
    timeWindowId: z.string(),
    fromDateTimeUtc: z.string(),
    toDateTimeUtc: z.string(),
    tspName: z.string().optional(),
    paymentCutOffDateTimeUtc: z.string().optional(),
    fulfillmentUnit: fulfillmentUnitSchema.optional(),
    // Provided service specific fields
    date: z.string().optional(),
    timeZone: z.string(),
});

export type TimeWindow = z.infer<typeof timeWindowSchema>;
